// src/components/StripeConnect.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserStripeAccount, updateUserStripeAccount, STRIPE_ONBOARDING_STATUS } from '../services/UserRolesService';
import { createConnectedAccount, createAccountLink, checkAccountStatus, createDashboardLink } from '../services/StripeConnectService';
import './StripeConnect.css';

function StripeConnect() {
  const { currentUser } = useAuth();
  const [stripeAccount, setStripeAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(STRIPE_ONBOARDING_STATUS.NOT_STARTED);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  // Load user's Stripe account info
  useEffect(() => {
    const loadStripeAccount = async () => {
      if (!currentUser) return;
      
      try {
        const accountInfo = await getUserStripeAccount(currentUser.uid);
        if (accountInfo) {
          setStripeAccount(accountInfo.stripeAccountId);
          setOnboardingStatus(accountInfo.onboardingStatus);
          
          // Check if account is complete if it's in progress
          if (accountInfo.stripeAccountId && accountInfo.onboardingStatus === STRIPE_ONBOARDING_STATUS.IN_PROGRESS) {
            await checkOnboardingComplete(accountInfo.stripeAccountId);
          }
        }
        setLoading(false);
      } catch (err) {
        console.error('Error loading Stripe account:', err);
        setError('Failed to load payment account information');
        setLoading(false);
      }
    };
    
    loadStripeAccount();
  }, [currentUser]);

  // Check if onboarding is complete
  const checkOnboardingComplete = async (accountId) => {
    setIsCheckingStatus(true);
    try {
      const status = await checkAccountStatus(accountId);
      if (status.isComplete) {
        await updateUserStripeAccount(currentUser.uid, accountId, STRIPE_ONBOARDING_STATUS.COMPLETED);
        setOnboardingStatus(STRIPE_ONBOARDING_STATUS.COMPLETED);
      }
    } catch (err) {
      console.error('Error checking account status:', err);
      // Don't show error for status check failures
    } finally {
      setIsCheckingStatus(false);
    }
  };

  // Start Stripe Connect onboarding
  const handleStartOnboarding = async () => {
    setIsCreatingAccount(true);
    setError(null);
    
    try {
      // Create connected account if doesn't exist
      let accountId = stripeAccount;
      if (!accountId) {
        try {
          accountId = await createConnectedAccount(currentUser.uid, currentUser.email);
          setStripeAccount(accountId);
        } catch (createError) {
          console.error('Account creation error:', createError);
          
          // Check for specific error types
          if (createError.message.includes('email')) {
            setError('There was an issue with your email address. Please ensure your email is valid and try again.');
          } else if (createError.message.includes('already exists')) {
            setError('A Stripe account already exists for this user. Please contact support if you need assistance.');
          } else {
            setError(`Failed to create payment account: ${createError.message}`);
          }
          
          setIsCreatingAccount(false);
          return;
        }
      }
      
      // Create account link for onboarding
      const refreshUrl = `${window.location.origin}/provider-dashboard`;
      const returnUrl = `${window.location.origin}/provider-dashboard?stripe=success`;
      
      try {
        const onboardingUrl = await createAccountLink(accountId, refreshUrl, returnUrl);
        
        // Redirect to Stripe onboarding
        window.location.href = onboardingUrl;
      } catch (linkError) {
        console.error('Account link error:', linkError);
        setError(`Failed to start onboarding process: ${linkError.message}`);
        setIsCreatingAccount(false);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred. Please try again or contact support.');
      setIsCreatingAccount(false);
    }
  };

  // Open Stripe Express dashboard
  const handleOpenDashboard = async () => {
    setError(null);
    
    try {
      const dashboardUrl = await createDashboardLink(stripeAccount);
      window.open(dashboardUrl, '_blank');
    } catch (err) {
      console.error('Error opening dashboard:', err);
      setError(`Failed to open dashboard: ${err.message}`);
    }
  };

  // Check for return from Stripe onboarding
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('stripe') === 'success' && stripeAccount) {
      checkOnboardingComplete(stripeAccount);
    }
  }, [stripeAccount]);

  if (loading) {
    return <div className="stripe-connect-loading">Loading payment settings...</div>;
  }

  return (
    <div className="stripe-connect-container">
      <div className="stripe-connect-header">
        <h2>Payment Settings</h2>
        <p>Connect your bank account to receive payments from your listings</p>
      </div>
      
      {error && (
        <div className="stripe-connect-error">
          <strong>Error:</strong> {error}
        </div>
      )}
      
      {onboardingStatus === STRIPE_ONBOARDING_STATUS.NOT_STARTED && (
        <div className="stripe-connect-setup">
          <div className="setup-info">
            <h3>Set up payments to receive earnings from each sale</h3>
            <ul>
              <li>Connect your bank account securely through Stripe</li>
              <li>Receive 80% of each transaction automatically (90% with Premium)</li>
              <li>RefoundEd retains 20% as a platform fee (10% with Premium)</li>
              <li>View your earnings and manage payouts anytime</li>
            </ul>
          </div>
          
          <button 
            onClick={handleStartOnboarding}
            disabled={isCreatingAccount}
            className="stripe-connect-button"
          >
            {isCreatingAccount ? 'Setting up...' : 'Set Up Payments'}
          </button>
          
          <p className="stripe-disclaimer">
            Powered by Stripe. Your financial information is securely handled by Stripe and never stored on our servers.
          </p>
          
          {/* Troubleshooting tips */}
          <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#f8f9fb', borderRadius: '8px' }}>
            <h4 style={{ marginTop: 0, color: '#666' }}>Having trouble?</h4>
            <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#666', fontSize: '0.9rem' }}>
              <li>Make sure you're using the email associated with your RefoundEd account</li>
              <li>Stripe Connect is only available for Canadian businesses at this time</li>
              <li>If you continue to experience issues, please contact support at info@refounded.ca</li>
            </ul>
          </div>
        </div>
      )}
      
      {onboardingStatus === STRIPE_ONBOARDING_STATUS.IN_PROGRESS && (
        <div className="stripe-connect-progress">
          <h3>Payment setup in progress</h3>
          <p>You haven't completed your payment setup yet. Complete it to start receiving payments.</p>
          
          <button 
            onClick={handleStartOnboarding}
            disabled={isCreatingAccount}
            className="stripe-connect-button"
          >
            {isCreatingAccount ? 'Loading...' : 'Continue Setup'}
          </button>
          
          {isCheckingStatus && <p className="checking-status">Checking account status...</p>}
        </div>
      )}
      
      {onboardingStatus === STRIPE_ONBOARDING_STATUS.COMPLETED && (
        <div className="stripe-connect-complete">
          <div className="success-icon">✓</div>
          <h3>Payment account connected!</h3>
          <p>You're all set to receive payments. You'll automatically receive 80% of each sale (90% with Premium subscription).</p>
          
          <div className="stripe-actions">
            <button 
              onClick={handleOpenDashboard}
              className="stripe-dashboard-button"
            >
              View Stripe Dashboard
            </button>
            
            <div className="payout-info">
              <h4>How payouts work:</h4>
              <ul>
                <li>You receive 80% of each transaction (90% with Premium)</li>
                <li>Funds are automatically transferred to your bank account</li>
                <li>Standard payout schedule is daily</li>
                <li>You can view all transactions in your Stripe dashboard</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StripeConnect;